/**
 * /university/patterns — pattern detection dashboard.
 *
 * Wraps the chaos-learning-service /learning/patterns endpoint:
 *   - Cluster grid (representative correction text + member count + success rate).
 *   - Click a cluster to expand and see the member attempt IDs.
 *   - Search box: for now this just filters the rendered clusters by
 *     substring on the representative text. Full semantic search would
 *     hit /learning/similar-tasks; that lives behind the dispatch UI.
 */
import type { PatternCluster } from '@chaos/types/learning';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ChevronDown, ChevronLeft, ChevronRight, Loader2, Network, Search } from 'lucide-react';
import * as React from 'react';

import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  effectivenessTone,
  fmtCount,
  fmtScore,
  TONE_DOT_CLASS,
  TONE_TEXT_CLASS,
} from '../../../components/university/formatters';
import { learningApi, learningKeys } from '../../../lib/api/learning';
import { cn } from '../../../lib/utils';

function PatternsRoute() {
  const [days, setDays] = React.useState(30);
  const [filter, setFilter] = React.useState('');

  const patternsQ = useQuery({
    queryKey: learningKeys.patterns({ days }),
    queryFn: () => learningApi.getPatterns({ days }),
    staleTime: 60_000,
  });

  const visibleClusters = React.useMemo(() => {
    const all = patternsQ.data?.clusters ?? [];
    const q = filter.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (c) =>
        c.representativeText.toLowerCase().includes(q) ||
        (c.skillName ?? '').toLowerCase().includes(q) ||
        (c.skillKey ?? '').toLowerCase().includes(q),
    );
  }, [patternsQ.data, filter]);

  return (
    <section className="mx-auto flex w-full max-w-5xl flex-col gap-4 p-6">
      <header className="flex flex-col gap-1">
        <Link
          to="/university"
          className="inline-flex w-fit items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3 w-3" aria-hidden /> Back to University
        </Link>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Network className="h-4 w-4 text-foreground" aria-hidden />
            <h1 className="text-[16px] font-semibold tracking-tight text-foreground">
              Pattern detection
            </h1>
            <span className="text-[12px] font-normal text-muted-foreground">
              {visibleClusters.length} {visibleClusters.length === 1 ? 'cluster' : 'clusters'}
              {patternsQ.data ? (
                <> · {fmtCount(patternsQ.data.totalAttemptsScanned)} attempts scanned</>
              ) : null}
            </span>
          </div>
          {patternsQ.isFetching ? (
            <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> syncing…
            </span>
          ) : null}
        </div>
        <p className="text-[12px] text-muted-foreground">
          Recurring correction themes detected across Tuner / founder feedback. High-count clusters
          with low success rates are the best candidates for the auto-improver.
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-3 rounded border border-border bg-background p-3">
        <div className="flex min-w-[220px] flex-1 flex-col gap-1">
          <Label
            htmlFor="pat-search"
            className="text-[11px] uppercase tracking-wider text-muted-foreground"
          >
            Filter
          </Label>
          <div className="relative">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              id="pat-search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="By correction text or skill name…"
              className="h-9 pl-7 text-[13px]"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <Label
            htmlFor="pat-window"
            className="text-[11px] uppercase tracking-wider text-muted-foreground"
          >
            Window
          </Label>
          <select
            id="pat-window"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className={cn(
              'h-9 rounded border border-border bg-background px-2 text-[13px]',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
            )}
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 365 days</option>
          </select>
        </div>
      </div>

      {patternsQ.isPending ? (
        <div className="px-2 py-4 text-[12px] text-muted-foreground">Detecting patterns…</div>
      ) : patternsQ.isError ? (
        <div className="rounded border border-destructive/50 bg-destructive/5 p-3 text-[12px] text-destructive">
          Failed to load patterns: {(patternsQ.error as Error).message}
        </div>
      ) : visibleClusters.length === 0 ? (
        <EmptyState hasFilter={filter.trim().length > 0} />
      ) : (
        <ul className="grid grid-cols-1 gap-2 md:grid-cols-2">
          {visibleClusters.map((cluster) => (
            <ClusterCard
              key={`${cluster.skillId ?? 'unknown'}-${cluster.clusterId}`}
              cluster={cluster}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function ClusterCard({ cluster }: { cluster: PatternCluster }) {
  const [expanded, setExpanded] = React.useState(false);
  const tone = effectivenessTone(cluster.successRate);
  return (
    <li className="flex flex-col gap-2 rounded border border-border bg-background p-3">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="-mx-1 -mt-1 flex w-full cursor-pointer items-center justify-between gap-2 rounded px-1 py-0.5 text-left hover:bg-muted/30"
      >
        <div className="flex items-center gap-2 text-[12px]">
          <span
            aria-hidden
            className={cn('h-1.5 w-1.5 shrink-0 rounded-full', TONE_DOT_CLASS[tone])}
          />
          <span className="font-semibold text-foreground">
            {cluster.skillName ?? 'Across skills'}
          </span>
          {cluster.skillKey ? (
            <span className="font-mono text-[11px] text-muted-foreground">{cluster.skillKey}</span>
          ) : null}
          <span className="rounded border border-border bg-muted/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
            {cluster.correctionKind}
          </span>
        </div>
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        )}
      </button>
      <p className="line-clamp-3 whitespace-pre-line text-[12px] text-foreground/85">
        {cluster.representativeText}
      </p>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
        <span>
          members{' '}
          <span className="font-mono font-medium tabular-nums text-foreground">
            {fmtCount(cluster.memberCount)}
          </span>
        </span>
        <span>
          success rate{' '}
          <span className={cn('font-mono font-medium tabular-nums', TONE_TEXT_CLASS[tone])}>
            {fmtScore(cluster.successRate)}
          </span>
        </span>
        {cluster.avgIterations != null ? (
          <span>
            avg iter{' '}
            <span className="font-mono font-medium tabular-nums text-foreground">
              {cluster.avgIterations.toFixed(1)}
            </span>
          </span>
        ) : null}
      </div>
      {expanded ? (
        <div className="mt-1 rounded border border-border bg-muted/20 p-2">
          <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
            Member attempt IDs
          </div>
          <ul className="flex max-h-32 flex-col gap-0.5 overflow-y-auto font-mono text-[10px] text-muted-foreground">
            {cluster.memberIds.slice(0, 200).map((id) => (
              <li key={id} className="truncate">
                {id}
              </li>
            ))}
            {cluster.memberIds.length > 200 ? (
              <li className="text-muted-foreground/70">
                …and {cluster.memberIds.length - 200} more.
              </li>
            ) : null}
          </ul>
          {cluster.skillId ? (
            <div className="mt-2 text-right">
              <Link
                to="/university/skills/$skillId"
                params={{ skillId: cluster.skillId }}
                className="text-[11px] text-foreground hover:underline"
              >
                Open skill →
              </Link>
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function EmptyState({ hasFilter }: { hasFilter: boolean }) {
  return (
    <div className="rounded border border-dashed border-border bg-muted/10 p-6 text-center text-[12px] text-muted-foreground">
      {hasFilter ? (
        <>No clusters match this filter.</>
      ) : (
        <>
          <p>No patterns detected yet.</p>
          <p className="mt-1 text-[11px] text-muted-foreground/70">
            Tuner / founder feedback hasn't accumulated enough corrections to cluster. Patterns need
            at least 3 corrections per cluster.
          </p>
        </>
      )}
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/university/patterns')({
  component: PatternsRoute,
});
